import React, { useState, useEffect, useRef } from 'react';
import { ModuloSistema, AcaoSistema, PermissaoPerfil } from '../../../../types/admin';

interface MatrizPermissoesProps {
  modulos: ModuloSistema[];
  acoes: AcaoSistema[];
  permissoesExistentes?: PermissaoPerfil[];
  onChange: (permissoes: { id_modulo: number; id_acao: number; permitido: boolean }[]) => void;
  readOnly?: boolean;
}

interface PermissaoState {
  [moduloId: number]: {
    [acaoId: number]: boolean;
  };
}

export const MatrizPermissoes: React.FC<MatrizPermissoesProps> = ({
  modulos,
  acoes,
  permissoesExistentes = [],
  onChange,
  readOnly = false
}) => {
  const [permissoes, setPermissoes] = useState<PermissaoState>({});
  const isInitialLoad = useRef(true);
  const scrollTopRef = useRef<HTMLDivElement>(null);
  const tableScrollRef = useRef<HTMLDivElement>(null);

  const acoesPermitidas = ['VISUALIZAR', 'CRIAR', 'EDITAR', 'EXCLUIR', 'MOVIMENTAR_KANBAN', 'VER_VALORES', 'APROVAR_PENDENTE'];
  const acoesFiltradas = acoes.filter(acao => acoesPermitidas.includes(acao.codigo));

  // Inicializar matriz de permissões
  useEffect(() => {
    if (modulos.length === 0 || acoes.length === 0) return;
    
    const novasPermissoes: PermissaoState = {};
    
    modulos.forEach(modulo => {
      novasPermissoes[modulo.id] = {};
      acoesFiltradas.forEach(acao => {
        // Buscar permissão existente
        const permissaoExistente = permissoesExistentes.find(p => 
          p.id_modulo === modulo.id && p.id_acao === acao.id
        );
        novasPermissoes[modulo.id][acao.id] = permissaoExistente?.permitido || false;
      });
    });
    
    setPermissoes(novasPermissoes);
  }, [modulos, acoes, permissoesExistentes]);

  // Notificar mudanças para o componente pai (evitando call na carga inicial)
  useEffect(() => {
    if (isInitialLoad.current) {
      isInitialLoad.current = false;
      return;
    }

    const permissoesArray: { id_modulo: number; id_acao: number; permitido: boolean }[] = [];
    
    // Para cada módulo, enviar permissões para TODAS as ações (não apenas as filtradas)
    modulos.forEach(modulo => {
      acoes.forEach(acao => {
        // Se a ação está sendo exibida na matriz, usar o valor atual
        // Se não está sendo exibida, manter a permissão existente
        let permitido = false;
        
        if (acoesPermitidas.includes(acao.codigo)) {
          // Ação filtrada - usar valor da matriz (garantindo que seja boolean)
          permitido = Boolean(permissoes[modulo.id]?.[acao.id]);
        } else {
          // Ação não filtrada - manter valor existente (garantindo que seja boolean)
          const permissaoExistente = permissoesExistentes.find(p => 
            p.id_modulo === modulo.id && p.id_acao === acao.id
          );
          permitido = Boolean(permissaoExistente?.permitido);
        }
        
        permissoesArray.push({
          id_modulo: modulo.id,
          id_acao: acao.id,
          permitido
        });
      });
    });
    
    // Só chama onChange se houver dados válidos e não for a carga inicial
    if (permissoesArray.length > 0 && typeof onChange === 'function') {
      
      // Verificar se há algum item inválido
      const itemsInvalidos = permissoesArray.filter(item => 
        typeof item.id_modulo !== 'number' || 
        typeof item.id_acao !== 'number' || 
        typeof item.permitido !== 'boolean'
      );
      if (itemsInvalidos.length > 0) {
        console.error('Items inválidos encontrados:', itemsInvalidos);
      }
      
      // Usar timeout para evitar problemas de sincronização
      const timeoutId = setTimeout(() => {
        onChange(permissoesArray);
      }, 0);
      
      return () => clearTimeout(timeoutId);
    }
  }, [permissoes, modulos, acoes, permissoesExistentes, acoesPermitidas, onChange]);

  // Alterar uma permissão específica
  const togglePermissao = (moduloId: number, acaoId: number) => {
    if (readOnly) return;
    
    setPermissoes(prev => ({
      ...prev,
      [moduloId]: {
        ...prev[moduloId],
        [acaoId]: !prev[moduloId]?.[acaoId]
      }
    }));
  };

  // Marcar/desmarcar toda uma linha (módulo)
  const toggleModulo = (moduloId: number, permitido: boolean) => {
    if (readOnly) return;
    
    const novasPermissoesModulo: { [key: number]: boolean } = {};
    acoesFiltradas.forEach(acao => {
      novasPermissoesModulo[acao.id] = permitido;
    });
    
    setPermissoes(prev => ({
      ...prev,
      [moduloId]: novasPermissoesModulo
    }));
  };

  // Marcar/desmarcar toda uma coluna (ação)
  const toggleAcao = (acaoId: number, permitido: boolean) => {
    if (readOnly) return;
    
    setPermissoes(prev => {
      const novasPermissoes = { ...prev };
      modulos.forEach(modulo => {
        novasPermissoes[modulo.id] = {
          ...novasPermissoes[modulo.id],
          [acaoId]: permitido
        };
      });
      return novasPermissoes;
    });
  };


  // Contar permissões de um módulo
  const contarPermissoesModulo = (moduloId: number) => {
    const permissoesModulo = permissoes[moduloId] || {};
    return Object.values(permissoesModulo).filter(Boolean).length;
  };

  // Verificar se todas as ações de um módulo estão permitidas
  const todasAcoesPermitidas = (moduloId: number) => {
    const permissoesModulo = permissoes[moduloId] || {};
    return acoesFiltradas.every(acao => permissoesModulo[acao.id]);
  };

  // Verificar se uma ação está permitida em todos os módulos
  const acaoPermitidaTodosModulos = (acaoId: number) => {
    return modulos.every(modulo => permissoes[modulo.id]?.[acaoId]);
  };

  // Sincronizar scroll entre barra superior e tabela
  useEffect(() => {
    const scrollTop = scrollTopRef.current;
    const tableScroll = tableScrollRef.current;

    if (!scrollTop || !tableScroll) return;

    const handleScrollTop = () => {
      if (tableScroll) {
        tableScroll.scrollLeft = scrollTop.scrollLeft;
      }
    };

    const handleScrollTable = () => {
      if (scrollTop) {
        scrollTop.scrollLeft = tableScroll.scrollLeft;
      }
    };

    scrollTop.addEventListener('scroll', handleScrollTop);
    tableScroll.addEventListener('scroll', handleScrollTable);

    return () => {
      scrollTop.removeEventListener('scroll', handleScrollTop);
      tableScroll.removeEventListener('scroll', handleScrollTable);
    };
  }, []);

  return (
    <div className="matriz-permissoes">
      <style>{`
        .matriz-permissoes .table-responsive::-webkit-scrollbar {
          height: 12px;
          width: 12px;
        }
        .matriz-permissoes .table-responsive::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 6px;
        }
        .matriz-permissoes .table-responsive::-webkit-scrollbar-thumb {
          background: #888;
          border-radius: 6px;
        }
        .matriz-permissoes .table-responsive::-webkit-scrollbar-thumb:hover {
          background: #555;
        }
        .matriz-permissoes .scroll-top::-webkit-scrollbar {
          height: 12px;
        }
        .matriz-permissoes .scroll-top::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 6px;
        }
        .matriz-permissoes .scroll-top::-webkit-scrollbar-thumb {
          background: #888;
          border-radius: 6px;
        }
        .matriz-permissoes .scroll-top::-webkit-scrollbar-thumb:hover {
          background: #555;
        }
      `}</style>
      {/* Controles Gerais */}
      {!readOnly && (
        <div className="row mb-3">
          <div className="col-md-6">
            <button
              type="button"
              className="btn btn-sm btn-success me-2"
              onClick={() => {
                const todasPermissoes: PermissaoState = {};
                modulos.forEach(modulo => {
                  todasPermissoes[modulo.id] = {};
                  acoesFiltradas.forEach(acao => {
                    todasPermissoes[modulo.id][acao.id] = true;
                  });
                });
                setPermissoes(todasPermissoes);
              }}
            >
              <i className="ti ti-check me-1"></i>
              Selecionar Todas
            </button>
            <button
              type="button"
              className="btn btn-sm btn-danger"
              onClick={() => {
                const nenhumaPermissao: PermissaoState = {};
                modulos.forEach(modulo => {
                  nenhumaPermissao[modulo.id] = {};
                  acoesFiltradas.forEach(acao => {
                    nenhumaPermissao[modulo.id][acao.id] = false;
                  });
                });
                setPermissoes(nenhumaPermissao);
              }}
            >
              <i className="ti ti-x me-1"></i>
              Desmarcar Todas
            </button>
          </div>
        </div>
      )}

      {/* Scroll superior */}
      <div 
        ref={scrollTopRef}
        className="scroll-top mb-2"
        style={{
          overflowX: 'auto',
          overflowY: 'hidden',
          height: '20px'
        }}
      >
        <div 
          style={{ 
            height: '1px', 
            width: `${250 + (acoesFiltradas.length * 120)}px`,
            minWidth: '100%'
          }}
        />
      </div>

      {/* Tabela de Permissões */}
      <div 
        ref={tableScrollRef}
        className="table-responsive"
        style={{
          maxHeight: '500px',
          overflowX: 'auto',
          overflowY: 'auto'
        }}
      >
        <table className="table table-bordered table-hover">
          <thead className="table-light">
            <tr>
              <th style={{ width: '30%' }}>
                <div className="d-flex align-items-center">
                  <i className="ti ti-box me-2"></i>
                  <strong>Módulos</strong>
                </div>
              </th>
              {acoesFiltradas.map(acao => (
                <th key={acao.id} className="text-center" style={{ minWidth: '120px' }}>
                  <div className="d-flex flex-column align-items-center">
                    <div className="d-flex align-items-center mb-1">
                      <i className={`ti-${getIconeAcao(acao.codigo)} me-1`}></i>
                      <span className="fw-bold">{acao.nome}</span>
                    </div>
                    {!readOnly && (
                      <div>
                        <button
                          type="button"
                          className={`btn btn-xs ${
                            acaoPermitidaTodosModulos(acao.id) ? 'btn-success' : 'btn-outline-success'
                          }`}
                          onClick={() => toggleAcao(acao.id, !acaoPermitidaTodosModulos(acao.id))}
                          title={acaoPermitidaTodosModulos(acao.id) ? 'Desmarcar todos' : 'Marcar todos'}
                        >
                          <i className={`ti-${acaoPermitidaTodosModulos(acao.id) ? 'minus' : 'plus'}`}></i>
                        </button>
                      </div>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {modulos.map(modulo => (
              <tr key={modulo.id} className="table-secondary">
                <td>
                  <div className="d-flex align-items-center justify-content-between">
                    <div className="d-flex align-items-center">
                      <div>
                        <div className="d-flex align-items-center">
                          <i className={`ti-${modulo.icone || 'box'} me-2`}></i>
                          <strong>{modulo.nome}</strong>
                        </div>
                        {modulo.descricao && (
                          <small className="text-muted d-block">{modulo.descricao}</small>
                        )}
                      </div>
                    </div>
                    <div className="d-flex align-items-center">
                      <span className="badge bg-primary me-2">
                        {contarPermissoesModulo(modulo.id)}/{acoesFiltradas.length}
                      </span>
                      {!readOnly && (
                        <button
                          type="button"
                          className={`btn btn-xs ${
                            todasAcoesPermitidas(modulo.id) ? 'btn-success' : 'btn-outline-success'
                          }`}
                          onClick={() => toggleModulo(modulo.id, !todasAcoesPermitidas(modulo.id))}
                          title={todasAcoesPermitidas(modulo.id) ? 'Desmarcar todas' : 'Marcar todas'}
                        >
                          <i className={`ti-${todasAcoesPermitidas(modulo.id) ? 'minus' : 'plus'}`}></i>
                        </button>
                      )}
                    </div>
                  </div>
                </td>
                {acoesFiltradas.map(acao => (
                  <td key={acao.id} className="text-center align-middle">
                    <div className="form-check d-flex justify-content-center">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        checked={permissoes[modulo.id]?.[acao.id] || false}
                        onChange={() => togglePermissao(modulo.id, acao.id)}
                        disabled={readOnly}
                      />
                    </div>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Resumo */}
      <div className="mt-3 p-3 bg-light rounded">
        <div className="row text-center">
          <div className="col-md-3">
            <div className="border-end">
              <h5 className="text-primary mb-0">{modulos.length}</h5>
              <small className="text-muted">Módulos</small>
            </div>
          </div>
          <div className="col-md-3">
            <div className="border-end">
              <h5 className="text-info mb-0">{acoesFiltradas.length}</h5>
              <small className="text-muted">Ações</small>
            </div>
          </div>
          <div className="col-md-3">
            <div className="border-end">
              <h5 className="text-success mb-0">
                {Object.values(permissoes).reduce((total, moduloPermissoes) => 
                  total + Object.values(moduloPermissoes).filter(Boolean).length, 0
                )}
              </h5>
              <small className="text-muted">Permitidas</small>
            </div>
          </div>
          <div className="col-md-3">
            <h5 className="text-danger mb-0">
              {modulos.length * acoesFiltradas.length - 
                Object.values(permissoes).reduce((total, moduloPermissoes) => 
                  total + Object.values(moduloPermissoes).filter(Boolean).length, 0
                )}
            </h5>
            <small className="text-muted">Negadas</small>
          </div>
        </div>
      </div>
    </div>
  );
};

// Helper para obter ícone baseado no código da ação
const getIconeAcao = (codigo: string): string => {
  const icones: Record<string, string> = {
    'VISUALIZAR': 'eye',
    'CRIAR': 'plus',
    'EDITAR': 'edit',
    'EXCLUIR': 'trash',
    'APROVAR': 'check',
    'APROVAR_PENDENTE': 'shield-check',
    'MOVIMENTAR_KANBAN': 'arrows-move',
    'VER_VALORES': 'currency-dollar',
    'EXPORTAR': 'download',
    'IMPORTAR': 'upload',
    'RELATORIOS': 'chart-bar'
  };
  
  return icones[codigo] || 'settings';
};